import { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EQUIPMENTS_ENDPOINT } from "../../../consts";
import CustomModal from "../modal/modal.component";
import EquipmentForm from "../equipmentForm/equipmentForm.component";
import CustomAutocomplete from "../customAutocomplete/customAutocomplete.component";
import AddIcon from "@mui/icons-material/Add";
import { formatAddress, highlightMatch } from "../../../utils";
import { useTenantName } from "../../../contexts/auth";

interface Equipment {
  id: string;
  name?: string;
  full_name?: string;
  installation_address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
    email_counter_reminders?: string;
  };
  [key: string]: any;
}

interface EquipmentAutocompleteBaseProps {
  disabled?: boolean;
  required?: boolean;
  size?: "small" | "medium";
  clientId?: string;
  filterUnattached?: boolean;
  onlyActiveContracts?: boolean;
  showAddEquipmentButton?: boolean;
  /**
   * Equipment ids to hide from the option list (e.g. already picked in a
   * sibling form row). The currently selected value is never hidden.
   */
  excludeIds?: (string | number)[];
  /**
   * When set, restricts options by whether the equipment has an installation
   * address: `true` shows only equipment with no address (e.g. for internal
   * jobs), `false` shows only equipment that has one. Omit to not filter by
   * address at all.
   */
  hasNoAddress?: boolean;
}

interface EquipmentAutocompleteSingleProps extends EquipmentAutocompleteBaseProps {
  multiple?: false;
  value: Equipment | null;
  onChange: (equipment: Equipment | null) => void;
}

interface EquipmentAutocompleteMultipleProps extends EquipmentAutocompleteBaseProps {
  multiple: true;
  value: Equipment[];
  onChange: (equipments: Equipment[]) => void;
}

type EquipmentAutocompleteProps =
  | EquipmentAutocompleteSingleProps
  | EquipmentAutocompleteMultipleProps;

export default function EquipmentAutocomplete(props: EquipmentAutocompleteProps) {
  const {
    disabled = false,
    required = false,
    size = "small",
    clientId,
    filterUnattached = true,
    onlyActiveContracts = false,
    showAddEquipmentButton = false,
    excludeIds,
    hasNoAddress,
    multiple = false,
  } = props;

  const { t } = useTranslation();
  // Equipment with no installation address sits with us, not at a client.
  const tenantName = useTenantName();

  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshAutocomplete, setRefreshAutocomplete] = useState(0);

  useEffect(() => {
    if (!filterUnattached) {
      setEquipments([]);
    }
  }, [clientId, filterUnattached, hasNoAddress]);

  const handleOptionsChange = useCallback((fetchedOptions: Equipment[]) => {
    setEquipments((prevEquipments) => {
      const merged = [...prevEquipments];
      fetchedOptions.forEach((newEquipment) => {
        if (!merged.find((equipment) => equipment.id === newEquipment.id)) {
          merged.push(newEquipment);
        }
      });
      return merged;
    });
  }, []);

  const handleEquipmentCreated = (newEquipment?: Equipment) => {
    setIsModalOpen(false);
    if (newEquipment) {
      setEquipments((prev) => {
        const exists = prev.find((e) => e.id === newEquipment.id);
        if (!exists) {
          return [...prev, newEquipment];
        }
        return prev;
      });

      if (multiple) {
        const currentValue = (props as EquipmentAutocompleteMultipleProps).value;
        const exists = currentValue.some((e) => String(e.id) === String(newEquipment.id));
        if (!exists) {
          (props as EquipmentAutocompleteMultipleProps).onChange([...currentValue, newEquipment]);
        }
      } else {
        (props as EquipmentAutocompleteSingleProps).onChange(newEquipment);
      }
    }
    setRefreshAutocomplete((prev) => prev + 1);
  };

  const noOptionsComponent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
        px: 1,
      }}
    >
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setIsModalOpen(true)}
      >
        {t("Add new equipment")}
      </Button>
    </Box>
  );

  const excludedIdSet = new Set((excludeIds ?? []).map((id) => String(id)));
  const currentValueIds = new Set(
    (multiple
      ? (props as EquipmentAutocompleteMultipleProps).value
      : [(props as EquipmentAutocompleteSingleProps).value]
    )
      .filter((equipment): equipment is Equipment => Boolean(equipment))
      .map((equipment) => String(equipment.id))
  );
  const filterOptions = (opts: Equipment[]) =>
    opts.filter(
      (opt) =>
        !excludedIdSet.has(String(opt.id)) || currentValueIds.has(String(opt.id))
    );

  const selectedOptions = multiple
    ? [...(props as EquipmentAutocompleteMultipleProps).value, ...equipments].filter(
        (equipment, index, all) =>
          all.findIndex((item) => String(item.id) === String(equipment.id)) === index
      )
    : equipments;

  return (
    <>
      <CustomAutocomplete
        key={`${clientId || "no-client"}-${filterUnattached ? "unattached" : "all"}-${hasNoAddress === undefined ? "any-address" : hasNoAddress}-${multiple ? "multiple" : "single"}`}
        multiple={multiple}
        size={size}
        apiConfig={{
          url: EQUIPMENTS_ENDPOINT,
          minSearchChars: 2,
          debounceMs: 300,
          queryParams: {
            ...(filterUnattached
              ? { unattached: "true" }
              : {
                  client: clientId || undefined,
                  ...(clientId && onlyActiveContracts ? { is_active: "true" } : {}),
                }),
            ...(hasNoAddress !== undefined ? { has_no_address: String(hasNoAddress) } : {}),
          },
        }}
        fetchTrigger={refreshAutocomplete}
        onOptionsChange={handleOptionsChange}
        filterOptions={filterOptions}
        options={selectedOptions}
        value={
          multiple
            ? (props as EquipmentAutocompleteMultipleProps).value
            : (props as EquipmentAutocompleteSingleProps).value || null
        }
        isOptionEqualToValue={(option, selectedValue) =>
          String(option.id) === String(selectedValue.id)
        }
        onChange={(_, newValue) => {
          if (multiple) {
            (props as EquipmentAutocompleteMultipleProps).onChange(
              (newValue || []) as Equipment[]
            );
          } else {
            (props as EquipmentAutocompleteSingleProps).onChange(
              (newValue || null) as Equipment | null
            );
          }
        }}
        onInputChange={(_, inputValue, reason) => {
          if (reason === "reset") {
            return;
          }

          setSearchValue(inputValue);
        }}
        getOptionLabel={(option) => option.full_name || option.name || ""}
        noOptionsText={t("No options")}
        disabled={disabled}
        slotProps={{
          listbox: {
            component: (listboxProps: any) => (
              <ul {...listboxProps}>
                {!searchValue && showAddEquipmentButton && noOptionsComponent}
                {listboxProps.children}
              </ul>
            ),
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Equipment")}
            required={required}
            multiline={!multiple}
            minRows={1}
            maxRows={4}
          />
        )}
        renderOption={(optionProps, option) => {
          const { key, ...otherProps } = optionProps;
          const address = formatAddress(option.installation_address || null) || tenantName;
          const reminderEmail = option.installation_address?.email_counter_reminders || "";
          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{ display: "flex", flexDirection: "column", py: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {highlightMatch(option.full_name || option.name || "", searchValue)}
              </Typography>
              {address && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                  {address}
                </Typography>
              )}
              {reminderEmail && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                  {`${t("Email for counter reminders")}: ${reminderEmail}`}
                </Typography>
              )}
            </Box>
          );
        }}
        // minWidth 0: as a flex item the root would otherwise refuse to shrink
        // below its content (a long equipment name or chip) and spill over
        // whatever sits next to it in the row.
        sx={{ flex: 1, minWidth: 0 }}
      />

      <CustomModal
        opened={isModalOpen}
        setOpened={setIsModalOpen}
        title={t("Add new equipment")}
        content={
          <EquipmentForm
            onSuccess={handleEquipmentCreated}
          />
        }
      />
    </>
  );
}
